import { FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getAllFirms } from "../../Services/Firm";
import { fetchPersonByFirm } from "../../Services/Person";
import { getReferralEstimatedFees, IReferralEstimatedFee } from "../../Services/ReferralEstimatedFees";
import { createReferral, getReferral, ReferralWriteBody, updateReferral } from "../../Services/Referral";
import { getReferralTypes, IReferralType } from "../../Services/ReferralTypes";
import ChevronRight from "../../components/Atomos/Icons/ChevronRight";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import IFirm from "../../interfaces/models/IFirm";
import { useAuth } from "../../store/AuthContext";
import StaffReferralForm, { firmLabel, PersonOption } from "./components/StaffReferralForm";
import { apiErrorMessage, inputDate } from "./referralFormat";
import {
  referralFormAccent,
  referralFormActionsClass,
  referralFormCancelClass,
  referralFormCheckboxClass,
  referralFormFieldClass,
  referralFormGridThreeClass,
  referralFormGridTwoClass,
  referralFormHintClass,
  referralFormTextareaClass,
  referralFormTitleRuleClass,
  ReferralLoading,
  referralMemberPageClass,
} from "./referralSurface";

type Variant = "member" | "staff";

type Props = {
  variant: Variant;
  mode: "add" | "edit";
  basePath: string;
  lockedFirmCosmosId?: string;
};

export default function ReferralForm({ variant, mode, basePath, lockedFirmCosmosId }: Props) {
  const params = useParams();
  const referralId = params.referralId || params.id;
  const navigate = useNavigate();
  const { loggedPerson } = useAuth();
  const [firms, setFirms] = useState<IFirm[]>([]);
  const [types, setTypes] = useState<IReferralType[]>([]);
  const [fees, setFees] = useState<IReferralEstimatedFee[]>([]);
  const [byPeople, setByPeople] = useState<PersonOption[]>([]);
  const [toPeople, setToPeople] = useState<PersonOption[]>([]);
  const [relationshipType, setRelationshipType] = useState<"referral" | "collaboration">("referral");
  const [isMultiYear, setIsMultiYear] = useState(false);
  const [referralDate, setReferralDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [firmByCosmosId, setFirmByCosmosId] = useState("");
  const [sendByCosmosId, setSendByCosmosId] = useState("");
  const [firmToCosmosId, setFirmToCosmosId] = useState("");
  const [sendToCosmosId, setSendToCosmosId] = useState("");
  const [referralTypeId, setReferralTypeId] = useState("");
  const [otherType, setOtherType] = useState("");
  const [estimatedFees, setEstimatedFees] = useState("");
  const [detail, setDetail] = useState("");
  const [storyId, setStoryId] = useState("");
  const [referralLegacyId, setReferralLegacyId] = useState("");
  const [catalogsReady, setCatalogsReady] = useState(false);
  const [error, setError] = useState("");
  const accent = referralFormAccent(variant);
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const location = useLocation();

  const byLabel = relationshipType === "collaboration" ? "Collaboration Member" : "Referred By";
  const toLabel = relationshipType === "collaboration" ? "Collaborated With" : "Referred To";
  const dateLabel = relationshipType === "collaboration" ? "Collaboration Date" : "Referral Date";

  useEffect(() => {
    let cancelled = false;
    async function loadCatalogs() {
      try {
        const [firmRows, typeRows, feeRows] = await Promise.all([
          getAllFirms(false),
          getReferralTypes(),
          getReferralEstimatedFees(true),
        ]);
        if (cancelled) {
          return;
        }
        setFirms(firmRows.filter((firm) => firm.membershipStatus !== "Withdrawn" && firm.membershipStatus !== "TemporaryWithdrawn"));
        setTypes(typeRows);
        setFees(feeRows);
      } catch (loadError) {
        if (!cancelled) {
          setError(apiErrorMessage(loadError, "The form could not be loaded."));
        }
      } finally {
        if (!cancelled) {
          setCatalogsReady(true);
        }
      }
    }
    loadCatalogs();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (variant !== "member") {
      return;
    }
    setPageMeta({
      title: "WSG Referrals & Collaborations",
      breadcrumbLabels: {
        [basePath]: "Firm Referrals",
        [location.pathname]: mode === "edit" ? "Edit Referral & Collaboration" : "Submit a Referral & Collaboration",
      },
    });
    return () => {
      clearPageMeta();
    };
  }, [variant, mode, basePath, location.pathname, setPageMeta, clearPageMeta]);

  useEffect(() => {
    if (mode === "add") {
      const initialFirm = lockedFirmCosmosId || loggedPerson?.firm || "";
      setFirmByCosmosId(initialFirm);
      setReferralDate(todayInputDate());
      return;
    }
    if (!referralId) {
      return;
    }
    let cancelled = false;
    getReferral(referralId).then((row) => {
      if (cancelled) {
        return;
      }
      const relationship = String(row.relationshipType || "").toLowerCase();
      setRelationshipType(relationship === "collaboration" || relationship === "colaboration" ? "collaboration" : "referral");
      setIsMultiYear(Boolean(row.isMultiYear));
      setReferralDate(inputDate(row.referralDate));
      setFromDate(inputDate(row.fromDate));
      setToDate(inputDate(row.toDate));
      setFirmByCosmosId(row.firmByCosmosId || "");
      setSendByCosmosId(row.sendByCosmosId || "");
      setFirmToCosmosId(row.firmToCosmosId || "");
      setSendToCosmosId(row.sendToCosmosId || "");
      setReferralTypeId(row.referralTypeId != null ? String(row.referralTypeId) : "");
      setEstimatedFees(row.estimatedFees ? String(row.estimatedFees) : "");
      setDetail(row.detail || "");
      setStoryId(row.storyId || "");
      setReferralLegacyId(row.legacyId != null ? String(row.legacyId) : "");
    }).catch((loadError) => {
      if (!cancelled) {
        setError(apiErrorMessage(loadError, "The referral could not be loaded."));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [mode, referralId, lockedFirmCosmosId, loggedPerson?.firm]);

  useEffect(() => {
    if (!firmByCosmosId) {
      setByPeople([]);
      return;
    }
    fetchPersonByFirm(firmByCosmosId).then((rows) => setByPeople(rows || []));
  }, [firmByCosmosId]);

  useEffect(() => {
    if (!firmToCosmosId) {
      setToPeople([]);
      return;
    }
    fetchPersonByFirm(firmToCosmosId).then((rows) => setToPeople(rows || []));
  }, [firmToCosmosId]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const body: ReferralWriteBody = {
      id: referralId,
      relationshipType,
      isMultiYear,
      referralDate: isMultiYear ? null : referralDate,
      fromDate: isMultiYear ? fromDate : null,
      toDate: isMultiYear ? toDate : null,
      firmByCosmosId,
      sendByCosmosId,
      firmToCosmosId,
      sendToCosmosId,
      referralTypeId: Number(referralTypeId),
      otherType,
      estimatedFees: Number(estimatedFees),
      detail,
      storyId: variant === "staff" ? storyId : undefined,
    };
    try {
      if (mode === "edit" && referralId) {
        await updateReferral(body);
      } else {
        await createReferral(body);
      }
      navigate(basePath);
    } catch (saveError) {
      setError(apiErrorMessage(saveError, "The referral could not be saved."));
    }
  }

  const firmOptions = [...firms].sort((left, right) =>
    firmLabel(left, variant === "staff").localeCompare(firmLabel(right, variant === "staff")),
  );

  if (variant === "staff") {
    return (
      <StaffReferralForm
        mode={mode}
        basePath={basePath}
        referralLegacyId={referralLegacyId}
        error={error}
        catalogsReady={catalogsReady}
        labels={{ by: byLabel, to: toLabel, date: dateLabel }}
        firms={firmOptions}
        types={types}
        fees={fees}
        byPeople={byPeople}
        toPeople={toPeople}
        values={{ relationshipType, isMultiYear, referralDate, fromDate, toDate, firmByCosmosId, sendByCosmosId, firmToCosmosId, sendToCosmosId, referralTypeId, otherType, estimatedFees, detail, storyId }}
        set={{
          relationshipType: setRelationshipType,
          isMultiYear: (checked) => {
            setIsMultiYear(checked);
            setReferralDate("");
            setFromDate("");
            setToDate("");
          },
          referralDate: setReferralDate,
          fromDate: setFromDate,
          toDate: setToDate,
          firmByCosmosId: (value) => { setFirmByCosmosId(value); setSendByCosmosId(""); },
          sendByCosmosId: setSendByCosmosId,
          firmToCosmosId: (value) => { setFirmToCosmosId(value); setSendToCosmosId(""); },
          sendToCosmosId: setSendToCosmosId,
          referralTypeId: setReferralTypeId,
          otherType: setOtherType,
          estimatedFees: setEstimatedFees,
          detail: setDetail,
          storyId: setStoryId,
        }}
        onSubmit={onSubmit}
      />
    );
  }

  return (
    <div className={referralMemberPageClass}>
      <div className={referralFormTitleRuleClass}>
        <h2 className={accent.title}>{mode === "edit" ? "Edit" : "Submit A New"} Referral Or Collaboration</h2>
      </div>
      <p className={accent.note}>All fields marked with <span className={accent.required}>*</span> are required</p>
      {error && <p className="mb-5 text-feedback-error-default">{error}</p>}
      {!catalogsReady ? <ReferralLoading /> : (
        <form className="flex flex-col gap-10" onSubmit={onSubmit}>
          <div className={referralFormGridThreeClass}>
            <Field label="Member Interaction" accent={accent} required>
              <select className={referralFormFieldClass} value={relationshipType === "collaboration" ? "1" : "2"} onChange={(event) => setRelationshipType(event.target.value === "1" ? "collaboration" : "referral")}>
                <option value="1">Collaboration</option>
                <option value="2">Referral</option>
              </select>
            </Field>
            <Field label={dateLabel} accent={accent} required>
              <div className="flex flex-col gap-2">
                <input className={referralFormFieldClass} type="date" disabled={isMultiYear} value={referralDate} onChange={(event) => setReferralDate(event.target.value)} />
                <label className={referralFormCheckboxClass}>
                  <input
                    type="checkbox"
                    checked={isMultiYear}
                    onChange={(event) => {
                      setIsMultiYear(event.target.checked);
                      setReferralDate("");
                      setFromDate("");
                      setToDate("");
                    }}
                  />
                  Multi-Year
                </label>
                <div className="flex items-center gap-3">
                  <input className={referralFormFieldClass} type="date" aria-label={`${dateLabel} From`} disabled={!isMultiYear} value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
                  <span aria-hidden="true">—</span>
                  <input className={referralFormFieldClass} type="date" aria-label={`${dateLabel} To`} disabled={!isMultiYear} value={toDate} onChange={(event) => setToDate(event.target.value)} />
                </div>
              </div>
            </Field>
            <div className="flex flex-col gap-6">
              <Field label="Type" accent={accent} required>
                <select className={referralFormFieldClass} value={referralTypeId} onChange={(event) => setReferralTypeId(event.target.value)}>
                  <option value="">Select an option</option>
                  {types.map((type) => (
                    <option key={type.id} value={type.typeId}>{type.description}</option>
                  ))}
                  <option value="0">Add Type</option>
                </select>
              </Field>
              {referralTypeId === "0" && (
                <Field label="Other Type" accent={accent} required>
                  <input className={referralFormFieldClass} type="text" value={otherType} onChange={(event) => setOtherType(event.target.value)} />
                </Field>
              )}
            </div>
          </div>
          <FirmContact
            title={byLabel}
            firms={firmOptions}
            people={byPeople}
            firmId={firmByCosmosId}
            personId={sendByCosmosId}
            accent={accent}
            onFirm={(value) => { setFirmByCosmosId(value); setSendByCosmosId(""); }}
            onPerson={setSendByCosmosId}
          />
          <FirmContact
            title={toLabel}
            firms={firmOptions}
            people={toPeople}
            firmId={firmToCosmosId}
            personId={sendToCosmosId}
            accent={accent}
            onFirm={(value) => { setFirmToCosmosId(value); setSendToCosmosId(""); }}
            onPerson={setSendToCosmosId}
          />
          <div className={referralFormGridTwoClass}>
            <Field label="Estimated Fees" accent={accent} required>
              <select className={referralFormFieldClass} value={estimatedFees} onChange={(event) => setEstimatedFees(event.target.value)}>
                <option value="">Select an option</option>
                {fees.map((fee) => (
                  <option key={fee.rangeId} value={fee.rangeId}>{fee.description}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Detail" accent={accent} required hint="Maximum 6,000 Characters (Including Spaces)">
            <textarea className={referralFormTextareaClass} maxLength={6000} value={detail} onChange={(event) => setDetail(event.target.value)} />
          </Field>
          <div className={referralFormActionsClass}>
            <button className={accent.submit} type="submit">
              Submit
              <ChevronRight className="h-4 w-4 shrink-0" />
            </button>
            <Link className={referralFormCancelClass} to={basePath}>
              Cancel
              <ChevronRight className="h-4 w-4 shrink-0" />
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}

type FormAccent = ReturnType<typeof referralFormAccent>;

function Field({ label, accent, required, hint, children }: { label: string; accent: FormAccent; required?: boolean; hint?: string; children: JSX.Element }) {
  return (
    <div>
      <label className={accent.label}>
        {label}
        {required && <span className={accent.required}> *</span>}
        {hint && <span className={referralFormHintClass}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function FirmContact({ title, firms, people, firmId, personId, accent, onFirm, onPerson }: { title: string; firms: IFirm[]; people: PersonOption[]; firmId: string; personId: string; accent: FormAccent; onFirm: (value: string) => void; onPerson: (value: string) => void }) {
  return (
    <div>
      <span className={accent.section}>{title}</span>
      <div className={referralFormGridTwoClass}>
        <Field label="Firm" accent={accent} required>
          <select className={referralFormFieldClass} value={firmId} onChange={(event) => onFirm(event.target.value)}>
            <option value="">-- Please Select --</option>
            {firms.map((firm) => (
              <option key={firm.id} value={firm.id}>{firmLabelOf(firm)}</option>
            ))}
          </select>
        </Field>
        <Field label="Contact" accent={accent} required>
          <select className={referralFormFieldClass} value={personId} onChange={(event) => onPerson(event.target.value)}>
            <option value="">-- Please Select --</option>
            {people.map((person) => (
              <option key={person.id} value={person.id}>{[person.name, person.lastName].filter(Boolean).join(" ")}</option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}

function todayInputDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function firmLabelOf(firm: IFirm): string {
  return firmLabel(firm, false);
}
